import { Alert, Button, Field, Modal } from '@catering/ui';
import { useMutation } from '@tanstack/react-query';
import { type ChangeEvent, type JSX, useState } from 'react';

import {
  confirmScannedSignature,
  requestScannedSignatureUpload,
  uploadScannedSignatureFile,
  type DeliveryNote,
} from '../../../delivery-notes/api';
import { mapDeliveryNoteError } from '../../../delivery-notes/delivery-note-api-errors';

const ALLOWED_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const RECEIVER_DOCUMENT_PATTERN = /^\d{7,8}$/;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export interface UploadSignedModalProps {
  note: DeliveryNote;
  onClose: () => void;
  onUploaded: () => void;
}

/**
 * `delivery-note-documents/spec.md` → "Carga de escaneo exitosa transiciona
 * el remito a SIGNED" (UI). Client-side type/size validation happens
 * before `requestScannedSignatureUpload` is ever called — the same
 * criterion `contract-document-tab.tsx` already applies, and this PR's own
 * TRIANGULATE: a file over 10 MB never round-trips to the server just to
 * be rejected there (`design.md` §7.3's own limit).
 */
export function UploadSignedModal({
  note,
  onClose,
  onUploaded,
}: UploadSignedModalProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | undefined>(undefined);
  const [receiverName, setReceiverName] = useState('');
  const [receiverDocument, setReceiverDocument] = useState('');
  const [receiverRole, setReceiverRole] = useState('');
  const [progress, setProgress] = useState<number | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (file === null) {
        throw new Error('missing file');
      }

      const { uploadUrl, key } = await requestScannedSignatureUpload(note.id, file.type, file.size);
      await uploadScannedSignatureFile(uploadUrl, file, setProgress);

      return confirmScannedSignature(note.id, {
        key,
        receiverName,
        receiverDocument: receiverDocument.trim() === '' ? undefined : receiverDocument.trim(),
        receiverRole: receiverRole.trim() === '' ? undefined : receiverRole,
      });
    },
    onSuccess: () => {
      setProgress(null);
      onUploaded();
    },
    onError: () => setProgress(null),
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    setClientError(undefined);
    const selected = event.target.files?.[0];

    if (selected === undefined) {
      setFile(null);
      return;
    }

    if (!ALLOWED_CONTENT_TYPES.includes(selected.type)) {
      setClientError('El archivo debe ser un PDF, JPEG o PNG.');
      setFile(null);
      return;
    }

    if (selected.size > MAX_SIZE_BYTES) {
      setClientError('El archivo supera el tamaño máximo permitido (10 MB).');
      setFile(null);
      return;
    }

    setFile(selected);
  }

  const documentIsValid =
    receiverDocument.trim() === '' || RECEIVER_DOCUMENT_PATTERN.test(receiverDocument.trim());
  const canSubmit =
    file !== null && receiverName.trim() !== '' && documentIsValid && !uploadMutation.isPending;

  return (
    <Modal open onClose={onClose} title="Cargar remito firmado" className="max-w-md">
      <div className="flex flex-col gap-4">
        {clientError !== undefined ? <Alert tone="danger">{clientError}</Alert> : null}
        {uploadMutation.isError ? (
          <Alert tone="danger">{mapDeliveryNoteError(uploadMutation.error)}</Alert>
        ) : null}

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-slate-900">Archivo (PDF, JPEG o PNG, hasta 10 MB)</span>
          <input
            type="file"
            aria-label="Archivo"
            accept="application/pdf,image/jpeg,image/png"
            onChange={handleFileChange}
            className="text-sm"
          />
        </label>

        <Field
          label="Nombre de quien recibe"
          name="receiverName"
          required
          value={receiverName}
          onChange={(event) => setReceiverName(event.target.value)}
        />
        <Field
          label="Documento"
          name="receiverDocument"
          inputMode="numeric"
          hint="DNI, 7 u 8 dígitos (opcional)"
          {...(documentIsValid ? {} : { error: 'El DNI debe tener 7 u 8 dígitos.' })}
          value={receiverDocument}
          onChange={(event) => setReceiverDocument(event.target.value)}
        />
        <Field
          label="Cargo"
          name="receiverRole"
          hint="Por ejemplo: encargado, recepción (opcional)"
          value={receiverRole}
          onChange={(event) => setReceiverRole(event.target.value)}
        />

        {progress !== null ? (
          <div className="flex flex-col gap-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-slate-900 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">Subiendo… {progress}%</p>
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            loading={uploadMutation.isPending}
            disabled={!canSubmit}
            onClick={() => uploadMutation.mutate()}
          >
            Confirmar carga
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
        </div>
      </div>
    </Modal>
  );
}
